import {
  FirebaseStorage,
  type FullMetadata,
  getBlob,
  getDownloadURL,
  getMetadata,
  listAll,
  ref,
  StorageError,
  StorageErrorCode,
  uploadBytes,
  uploadBytesResumable,
  type UploadMetadata,
  UploadTaskSnapshot,
} from "firebase/storage"

import Firebase from "./Firebase"

const STORAGE_MISS_CODES: ReadonlySet<string> = new Set([
  `storage/${StorageErrorCode.OBJECT_NOT_FOUND}`,
  `storage/${StorageErrorCode.UNAUTHORIZED}`,
])

export const isStorageMiss = (error: unknown): boolean =>
  error instanceof StorageError && STORAGE_MISS_CODES.has(error.code)

class FirestoreSingleton {
  private readonly storage: FirebaseStorage

  constructor(firestore?: FirebaseStorage) {
    this.storage = firestore ?? Firebase.getInstance().storage
  }

  uploadFile = async (
    file: File,
    path: string,
    metadata: Record<string, unknown>,
    onProgress: (snapshot: UploadTaskSnapshot) => void,
    onSuccess: (url: string) => void,
    onError?: (error: Error) => void
  ) => {
    const storageRef = ref(this.storage, path)
    const uploadTask = uploadBytesResumable(storageRef, file, metadata)

    const handleError = (error: Error) => {
      console.error("❌ Firestore.uploadFile error => ", error)
      if (onError) {
        onError(error)
      } else {
        throw error
      }
    }

    // getDownloadURL can fail after a successful upload; route that through
    // handleError too, so a promisified caller's reject always fires and the
    // upload can never hang unsettled.
    const handleSuccess = async () => {
      try {
        const url = await getDownloadURL(uploadTask.snapshot.ref)
        onSuccess(url)
      } catch (error) {
        handleError(
          error instanceof Error
            ? error
            : new Error("Could not resolve the uploaded file's download URL")
        )
      }
    }

    uploadTask.on("state_changed", onProgress, handleError, handleSuccess)
  }

  // Promise-based sibling of uploadFile for generated blobs (artwork
  // renditions). Resolves to the download URL once the upload completes.
  uploadBlob = async (
    blob: Blob,
    path: string,
    metadata: UploadMetadata
  ): Promise<string> => {
    const storageRef = ref(this.storage, path)
    const snapshot = await uploadBytes(storageRef, blob, metadata)
    return getDownloadURL(snapshot.ref)
  }

  getDownloadURL = async (path: string) => {
    const storageRef = ref(this.storage, path)
    return getDownloadURL(storageRef)
  }

  // Existence probe for convention-addressed objects. A missing or unreadable
  // object is a legitimate "no rendition" answer; anything else still throws.
  getDownloadURLOrNull = async (path: string): Promise<string | null> => {
    try {
      return await getDownloadURL(ref(this.storage, path))
    } catch (error) {
      if (isStorageMiss(error)) return null
      throw error
    }
  }

  getAppBucket = (): string | null =>
    this.storage.app.options.storageBucket ?? null

  getAllFiles = async (path: string) => {
    const storageRef = ref(this.storage, path)
    const files = await listAll(storageRef)
    console.log("✅🔥 files => ", files)
    const paths = files.items.map((item) => item.fullPath)
    console.log("✅🔥 paths => ", paths)
    return files
  }

  getFolderNames = async (path: string) => {
    const storageRef = ref(this.storage, path)
    const result = await listAll(storageRef)

    // Debug: log the raw prefix objects to see what properties are available
    console.log("✅🔥 raw prefixes => ", result.prefixes)
    console.log(
      "✅🔥 first prefix keys => ",
      Object.keys(result.prefixes[0] || {})
    )
    console.log("✅🔥 first prefix => ", result.prefixes[0])

    const folderInfo = result.prefixes.map((prefix) => ({
      name: prefix.name,
      fullPath: prefix.fullPath,
    }))
    console.log("✅🔥 folder info => ", folderInfo)
    return folderInfo
  }

  getAllFilesRecursive = async (path: string) => {
    const storageRef = ref(this.storage, path)
    const result = await listAll(storageRef)

    let allFiles = [...result.items]

    // Recursively get files from all subdirectories
    for (const prefix of result.prefixes) {
      const subFiles = await this.getAllFilesRecursive(prefix.fullPath)
      allFiles = [...allFiles, ...subFiles]
    }

    console.log("✅🔥 all files recursively => ", allFiles)
    return allFiles
  }

  getFilesWithUrls = async (folderPath: string = "/demoImages") => {
    const storageRef = ref(this.storage, folderPath)
    const result = await listAll(storageRef)
    console.log("✅🔥 result => ", result)

    // Get download URLs for all files in the folder
    const filesWithUrls = await Promise.all(
      result.items.map(async (fileRef) => {
        const url = await getDownloadURL(fileRef)
        return {
          name: fileRef.name,
          fullPath: fileRef.fullPath,
          url: url,
        }
      })
    )

    console.log("✅🔥 files with URLs => ", filesWithUrls)
    return filesWithUrls
  }

  getMetadata = async (path: string) => {
    const storageRef = ref(this.storage, path)
    return getMetadata(storageRef)
  }

  // Idempotency probe for the rendition backfill: "already there" is a normal
  // answer, so only a genuine miss maps to null; other failures still throw.
  getMetadataOrNull = async (path: string): Promise<FullMetadata | null> => {
    try {
      return await getMetadata(ref(this.storage, path))
    } catch (error) {
      if (isStorageMiss(error)) return null
      throw error
    }
  }

  // Reads an object's bytes through the SDK (same auth + CORS path as every
  // other Storage call) so the backfill can re-encode originals in the browser.
  getBlob = async (path: string): Promise<Blob> => {
    return getBlob(ref(this.storage, path))
  }
}

class Firestore {
  private static instance: FirestoreSingleton | null = null

  constructor(storage: FirebaseStorage) {
    if (Firestore.instance == null) {
      Firestore.instance = new FirestoreSingleton(storage)
    }
  }

  public static getInstance(): FirestoreSingleton {
    if (Firestore.instance == null) {
      Firestore.instance = new FirestoreSingleton(
        Firebase.getInstance().storage
      )
    }
    return Firestore.instance
  }
}

export default Firestore
